import { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { AuthTokens } from '../types/auth.types';
import { UserInfo } from '../types/jwt.types';
import { AuthMethod } from '../services/tokenStorageService';
import { AuthenticationService } from '../services/authenticationService';
import { isAuthError } from '../types/errors.types';

export interface AuthenticationState {
  isAuthenticated: boolean;
  isLoading: boolean;
  tokens: AuthTokens | null;
  userInfo: UserInfo | null;
  authMethod: AuthMethod | null;
  error: string | null;
}

export interface AuthenticationActions {
  refreshTokens: () => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

export function useAuthenticationState(): AuthenticationState & AuthenticationActions {
  const [state, setState] = useState<AuthenticationState>({
    isAuthenticated: false,
    isLoading: true,
    tokens: null,
    userInfo: null,
    authMethod: null,
    error: null,
  });

  const { instance } = useMsal();
  const authService = AuthenticationService.getInstance();

  useEffect(() => {
    authService.initialize(instance);
    initializeAuthState();
  }, [instance]);

  const initializeAuthState = async (): Promise<void> => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      // Check for stored authentication data
      const storedAuth = authService.getStoredAuthenticationData();
      
      if (storedAuth) {
        setState({
          isAuthenticated: true,
          isLoading: false,
          tokens: storedAuth.tokens,
          userInfo: storedAuth.userInfo,
          authMethod: storedAuth.authMethod,
          error: null,
        });
        return;
      }

      // Check for redirect response
      const redirectResult = await authService.handleAuthenticationResponse();
      
      if (redirectResult) {
        authService.saveAuthenticationData(redirectResult, 'direct');
        setState({
          isAuthenticated: true,
          isLoading: false,
          tokens: redirectResult.tokens,
          userInfo: redirectResult.userInfo,
          authMethod: 'direct',
          error: null,
        });
        return;
      }

      // No authentication found
      setState(prev => ({
        ...prev,
        isAuthenticated: false,
        isLoading: false,
      }));

    } catch (error) {
      console.error('Authentication initialization failed:', error);
      
      let errorMessage = 'Authentication initialization failed';
      if (isAuthError(error)) {
        errorMessage = error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      setState({
        isAuthenticated: false,
        isLoading: false,
        tokens: null,
        userInfo: null,
        authMethod: null,
        error: errorMessage,
      });
    }
  };

  const refreshTokens = async (): Promise<void> => {
    try {
      setState(prev => ({ ...prev, error: null }));

      const scopes = [
        'api://6ef233b2-6c49-4272-a072-5bc6ae2855e8/access_as_user',
        'openid',
        'profile',
        'email'
      ];

      const result = await authService.acquireTokenSilently(scopes);
      const currentAuthMethod = state.authMethod || 'direct';

      authService.saveAuthenticationData(result, currentAuthMethod);

      setState(prev => ({
        ...prev,
        tokens: result.tokens,
        userInfo: result.userInfo,
      }));

    } catch (error) {
      console.error('Token refresh failed:', error);
      
      let errorMessage = 'Failed to refresh authentication tokens';
      if (isAuthError(error)) {
        errorMessage = error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      setState(prev => ({ ...prev, error: errorMessage }));
    }
  };

  const logout = async (): Promise<void> => {
    try {
      setState(prev => ({ ...prev, error: null }));
      
      await authService.logout();
      
      setState({
        isAuthenticated: false,
        isLoading: false,
        tokens: null,
        userInfo: null,
        authMethod: null,
        error: null,
      });

    } catch (error) {
      console.error('Logout failed:', error);
      
      // Even if logout fails, clear local state
      authService.clearStoredAuthenticationData();
      
      setState({
        isAuthenticated: false,
        isLoading: false,
        tokens: null,
        userInfo: null,
        authMethod: null,
        error: error instanceof Error ? error.message : 'Logout failed',
      });
    }
  };

  const clearError = (): void => {
    setState(prev => ({ ...prev, error: null }));
  };

  return {
    ...state,
    refreshTokens,
    logout,
    clearError,
  };
}